import React, { useState, useEffect } from 'react';
import { Item, Board, useCanvasStore } from '@/stores/canvasStore';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { Json } from '@/integrations/supabase/types';
import {
  ExternalLink, 
  FileText, 
  File, 
  Download, 
  Film, 
  Clock, 
  Pin, 
  Pencil, 
  X,
  StickyNote,
  Type,
  Image as ImageIcon,
  Link2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { BoardPicker } from './BoardPicker';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface WallPreviewModalProps {
  item: Item;
  board?: Board;
  onClose: () => void;
  onUpdate?: () => void;
}

const stickyColors: Record<string, string> = {
  yellow: 'hsl(45 93% 70%)',
  blue: 'hsl(210 85% 80%)',
  pink: 'hsl(340 85% 82%)',
  green: 'hsl(142 65% 72%)',
  gray: 'hsl(220 14% 82%)',
};

const typeLabels: Record<string, string> = {
  image: 'Image',
  sticky: 'Note',
  text_card: 'Text',
  document: 'Document',
  file: 'File',
  link: 'Link',
  video: 'Video',
};

const TypeIcon = ({ type, className }: { type: string; className?: string }) => {
  const iconProps = { className: cn('w-4 h-4', className) };
  switch (type) {
    case 'image': return <ImageIcon {...iconProps} />;
    case 'sticky': return <StickyNote {...iconProps} />;
    case 'text_card': return <Type {...iconProps} />;
    case 'document': return <FileText {...iconProps} />;
    case 'file': return <File {...iconProps} />;
    case 'link': return <Link2 {...iconProps} />;
    case 'video': return <Film {...iconProps} />;
    default: return null;
  }
};

export function WallPreviewModal({ item, board, onClose, onUpdate }: WallPreviewModalProps) {
  const { navigateToBoard, boards, setViewMode, updateItem, getVisibleBoards } = useCanvasStore();
  const content = item.content as Record<string, unknown>;
  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(item.board_id);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState<Record<string, unknown>>(content);
  const [isSaving, setIsSaving] = useState(false);
  
  // Check is_pinned_wall from content or from item directly (via DB column)
  const itemContent = item.content as Record<string, unknown>;
  const isPinned = (itemContent.is_pinned_wall as boolean) || false;
  const isEditable = ['sticky', 'text_card', 'document'].includes(item.item_type);

  // Reset editing state when item changes
  useEffect(() => {
    setEditedContent(item.content as Record<string, unknown>);
    setIsEditing(false);
  }, [item.id]);

  const handleGoToBoard = () => {
    if (board) {
      const targetBoard = boards.find(b => b.id === board.id);
      if (targetBoard) {
        setViewMode('focus');
        navigateToBoard(targetBoard);
      }
    }
    onClose();
  };

  const handleSelectBoard = async (boardId: string) => {
    const { error } = await supabase
      .from('items')
      .update({ board_id: boardId })
      .eq('id', item.id);

    if (error) {
      toast.error('Failed to update board');
      return;
    }

    const selectedBoard = boards.find(b => b.id === boardId);
    toast.success(`Added to "${selectedBoard?.name}"`);
    setSelectedBoardId(boardId);
    onUpdate?.();
  };

  const handleRemoveFromBoard = async () => {
    const homeBoard = boards.find(b => b.parent_id === null);
    if (!homeBoard) {
      toast.error('No home board found');
      return;
    }

    const { error } = await supabase
      .from('items')
      .update({ board_id: homeBoard.id })
      .eq('id', item.id);

    if (error) {
      toast.error('Failed to remove from board');
      return;
    }

    toast.success('Moved to Home');
    setSelectedBoardId(homeBoard.id);
    onUpdate?.();
  };

  const handleTogglePin = async () => {
    const newPinned = !isPinned;
    const { error } = await supabase
      .from('items')
      .update({ 
        is_pinned_wall: newPinned,
        pinned_wall_at: newPinned ? new Date().toISOString() : null
      })
      .eq('id', item.id);

    if (error) {
      toast.error('Failed to update pin status');
      return;
    }

    toast.success(newPinned ? 'Pinned to Wall' : 'Unpinned');
    onUpdate?.();
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      await updateItem(item.id, { content: editedContent as unknown as Json });
      toast.success('Saved');
      setIsEditing(false);
      onUpdate?.();
    } catch (err) {
      console.error('Failed to save:', err);
      toast.error('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedContent(content);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancel();
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSave();
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const renderContent = () => {
    switch (item.item_type) {
      case 'image':
        return (
          <div className="flex items-center justify-center">
            <img
              src={content.url as string}
              alt={content.alt as string || 'Image'}
              className="max-h-[70vh] max-w-full object-contain rounded-lg"
            />
          </div>
        );

      case 'sticky':
        const color = (content.color as string) || 'yellow';
        const bgColor = stickyColors[color] || stickyColors.yellow;
        
        return isEditing ? (
          <textarea
            value={(editedContent.text as string) || ''}
            onChange={(e) => setEditedContent({ ...editedContent, text: e.target.value })}
            onKeyDown={handleKeyDown}
            className="w-full h-48 p-4 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary"
            style={{ backgroundColor: bgColor, color: '#1F2937' }}
            autoFocus
            placeholder="Write your note..."
          />
        ) : (
          <div 
            className="p-6 rounded-lg min-h-[150px] cursor-text"
            style={{ backgroundColor: bgColor }}
            onClick={() => setIsEditing(true)}
          >
            <p className="whitespace-pre-wrap" style={{ color: '#1F2937' }}>
              {content.text as string || 'Empty note'}
            </p>
          </div>
        );

      case 'text_card':
        const textBody = ((content.body as string) || '').replace(/<[^>]*>/g, '');
        return isEditing ? (
          <textarea
            value={(editedContent.body as string) || ''}
            onChange={(e) => setEditedContent({ ...editedContent, body: e.target.value })}
            onKeyDown={handleKeyDown}
            className="w-full h-64 p-4 text-foreground bg-muted border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary"
            autoFocus
            placeholder="Write something..."
          />
        ) : (
          <div
            className="cursor-text"
            onClick={() => setIsEditing(true)}
          >
            <p className="whitespace-pre-wrap text-foreground">
              {textBody || 'Empty'}
            </p>
          </div>
        );

      case 'document':
        // Documents store content in 'body' field (HTML content)
        const docBody = (content.body as string) || (content.content as string) || '';
        const docText = docBody.replace(/<[^>]*>/g, '');
        
        return isEditing ? (
          <div className="space-y-4">
            <input
              type="text"
              value={(editedContent.title as string) || ''}
              onChange={(e) => setEditedContent({ ...editedContent, title: e.target.value })}
              className="w-full px-4 py-2 text-xl font-semibold bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Document title"
            />
            <textarea
              value={docBody.replace(/<[^>]*>/g, '')}
              onChange={(e) => setEditedContent({ ...editedContent, body: e.target.value })}
              onKeyDown={handleKeyDown}
              className="w-full h-64 p-4 text-foreground bg-muted border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              autoFocus
              placeholder="Document content..."
            />
          </div>
        ) : (
          <div 
            className="cursor-text"
            onClick={() => setIsEditing(true)}
          >
            <h2 className="text-2xl font-semibold mb-4 text-foreground">
              {content.title as string || 'Untitled'}
            </h2>
            <p className="whitespace-pre-wrap text-muted-foreground">
              {docText || 'Empty document'}
            </p>
          </div>
        );

      case 'file':
        return (
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-lg bg-gray-100 dark:bg-gray-800">
                <File className="h-8 w-8 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 dark:text-white text-lg">
                  {content.filename as string}
                </p>
                <p className="text-sm text-gray-500">
                  {formatFileSize(content.size as number || 0)}
                </p>
              </div>
            </div>
            <Button asChild>
              <a href={content.url as string} download target="_blank" rel="noopener noreferrer">
                <Download className="h-4 w-4 mr-2" />
                Download File
              </a>
            </Button>
          </div>
        );

      case 'video':
        const duration = content.duration as number || 0;
        const mins = Math.floor(duration / 60);
        const secs = Math.floor(duration % 60);
        const durationStr = `${mins}:${secs.toString().padStart(2, '0')}`;
        
        return (
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Film className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {content.filename as string || 'Video'}
              </h2>
              {duration > 0 && (
                <span className="flex items-center gap-1 text-sm text-gray-500 ml-2">
                  <Clock className="w-3 h-3" />
                  {durationStr}
                </span>
              )}
            </div>
            <video
              src={content.videoUrl as string}
              poster={content.thumbnailUrl as string || undefined}
              controls
              className="w-full max-h-[60vh] rounded-lg object-contain bg-black"
            >
              Your browser does not support the video tag.
            </video>
          </div>
        );

      case 'link':
        return (
          <div className="p-6 space-y-4">
            {content.image && (
              <img 
                src={content.image as string}
                alt={content.title as string || 'Link preview'}
                className="w-full h-48 object-cover rounded-lg"
              />
            )}
            <div>
              <p className="text-sm text-gray-500 mb-1">
                {content.siteName as string || ''}
              </p>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {content.title as string || content.url as string}
              </h2>
              {content.description && (
                <p className="text-gray-600 dark:text-gray-400">
                  {content.description as string}
                </p>
              )}
              <Button asChild className="mt-4">
                <a href={content.url as string} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open Link
                </a>
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const homeBoard = boards.find(b => b.parent_id === null);
  const isInHomeBoard = selectedBoardId === homeBoard?.id;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto p-0 bg-white dark:bg-[#2a2a2a] [&>button]:hidden">
        <div className="relative">
          {/* Header with type label, edit button, and close */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <TypeIcon type={item.item_type} />
              <span className="uppercase tracking-wide">{typeLabels[item.item_type] || item.item_type}</span>
            </div>
            
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancel}
                    className="text-gray-500"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                </>
              ) : (
                <>
                  {isEditable && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditing(true)}
                      className="text-gray-600 dark:text-gray-300"
                    >
                      <Pencil className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  )}
                  <button
                    onClick={handleTogglePin}
                    className={cn(
                      'p-2 rounded-lg transition-colors',
                      isPinned 
                        ? 'bg-amber-500 text-white' 
                        : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    )}
                    title={isPinned ? 'Unpin from Wall' : 'Pin to Wall'}
                  >
                    <Pin className="h-4 w-4" />
                  </button>
                  <button
                    onClick={onClose}
                    className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title="Close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="min-h-[200px] p-6">
            {renderContent()}
          </div>
          
          {/* Hint for editable items */}
          {isEditable && !isEditing && (
            <p className="text-center text-sm text-gray-400 dark:text-gray-500 pb-6">
              Click content to edit
            </p>
          )}
          
          {/* Board Picker Footer */}
          <BoardPicker
            boards={getVisibleBoards()}
            selectedBoardId={isInHomeBoard ? null : selectedBoardId}
            onSelectBoard={handleSelectBoard}
            onRemoveFromBoard={handleRemoveFromBoard}
          />

          {/* Go to Board button */}
          {board && !isInHomeBoard && (
            <div className="flex items-center justify-end p-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="outline" size="sm" onClick={handleGoToBoard}>
                <ExternalLink className="h-4 w-4 mr-2" />
                Go to Board
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
